<template>

<v-dialog v-model="show" id="set-allowance"
	persistent max-width="400px" :fullscreen="fullscreen">

	<v-card>

		<!--Modal Title -->
		<v-toolbar flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
			<v-toolbar-title>{{ title + ': ' + input.tokenSymbol }}</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn icon @click="show = false">
				<v-icon>mdi-window-close</v-icon>
			</v-btn>
		</v-toolbar>

		<!-- Modal Contents -->
		<v-card-text class="pa-5">

			<!-- Intro -->
			<div v-show="!txHash" class="mb-2 pa-3 text-center">
				<div v-if="!input.tokenAllowance" class="caption">
					{{ $t('compSetAllowance.introSet') }}
				</div>
				<div v-else>
					{{ $t('compSetAllowance.confirmRemove', {tokenSymbol: input.tokenSymbol}) }}
				</div>
			</div>

			<!-- Action buttons -->
			<div class="text-center my-2" v-show="!txHash">
				<v-btn color="primary"
					v-show="!input.tokenAllowance"
					@click="setUnlimitedProxyAllowance()"
					:disabled="loading"
				>
					{{ $t('compSetAllowance.buttonTextSet') }}: {{ input.tokenSymbol }} <ButtonLoading v-show="loading" class="ml-3" />
				</v-btn>

				<v-btn color="warning"
					v-show="input.tokenAllowance"
					@click="removeAllowance()"
					:disabled="loading"
				>
					{{ $t('compSetAllowance.buttonTextRemove') }}: {{ input.tokenSymbol }} <ButtonLoading v-show="loading" class="ml-3" />
				</v-btn>

			</div>

			<!-- Pending Transaction -->
			<div class="text-center my-2 mx-2" v-if="txHash">
				<PendingTransaction :txhash="txHash" :txurl="networkConfig.txURL"></PendingTransaction>
			</div>

		</v-card-text>

		<v-card-actions v-if="fullscreen">
			<v-spacer></v-spacer>
			<v-btn color="primary" outlined text class="mr-3" @click="show = false">
				<v-icon>mdi-window-close</v-icon> {{ $t('common.verb.close')}}
			</v-btn>
		</v-card-actions>

	</v-card>
</v-dialog>

</template>

<script>

import { config } from '@/config'
import { ERC20TokenContract } from '@0x/contract-wrappers'
import { BigNumber } from '@0x/utils'
import { Web3Wrapper } from '@0x/web3-wrapper'
import PendingTransaction from "@/components/PendingTransaction"
import {mapActions, mapGetters, mapState} from 'vuex'
import { UNLIMITED_ALLOWANCE_IN_BASE_UNITS } from "@/config/constants"
import ButtonLoading from "@/components/ButtonLoading"
import { getCurrentGasPrice } from '@/utils/utils'

export default {
	name: 'SetProxyAllowance',
	components: {
		PendingTransaction,
		ButtonLoading
	},
	props: {
		fullscreen: { type: Boolean, default: false},
	},
	data () {
		return {
			input: {
				tokenSymbol: null,
				tokenAddress: null,
				tokenDecimals: null,
				tokenAllowance: null,
			},
			title: '',
			loading: false,
			show: false,
			txHash: null,
		}
	},
	computed: {
		...mapGetters({
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			web3Wrapper: 'getWeb3Wrapper',
			providerEngine: 'getProviderEngine',
			contractWrappers: 'getContractWrappers',
			contractAddresses: 'getContractAddresses',
			networkConfig: 'getNetworkConfig'
		}),
	},
	methods: {
		...mapActions(['loadAccount']),
		async init () {
		},
		async showForm (token, currentAllowance) {

			this.loading = false
			this.txHash = null

			this.input.tokenAddress = token.address
			this.input.tokenSymbol = token.symbol
			this.input.tokenDecimals = token.decimals
			this.input.tokenAllowance = currentAllowance

			if (this.input.tokenAllowance) {
				this.title = this.$t('compSetAllowance.titleRemove')
			}
			else {
				this.title = this.$t('compSetAllowance.titleSet')
			}
			this.show = true
		},

		async setUnlimitedProxyAllowance () {
			// console.log('setUnlimitedProxyAllowance')
			const tokenAddress = this.input.tokenAddress
			if (!tokenAddress) {
				throw new Error ("Cannot get contract address")
			}
			const myAddress = this.myAddress

			// set unlimited allowance
			this.loading = true
			try {
				const token = new ERC20TokenContract(tokenAddress, this.providerEngine);
				const gasPrice = await getCurrentGasPrice(this.network.id)
				// console.log('gasPrice: ', gasPrice)
				this.txHash = await token
					.approve(this.contractWrappers.contractAddresses.erc20Proxy, UNLIMITED_ALLOWANCE_IN_BASE_UNITS)
					.sendTransactionAsync({
						from: myAddress,
						gasPrice: gasPrice
					})
				await this.web3Wrapper.awaitTransactionMinedAsync(this.txHash)
				await this.loadAccount(true)
				this.$parent.$emit("successNotification", this.$t('compSetAllowance.successSetMessage', {tokenSymbol: this.input.tokenSymbol}))
			} catch (err) {
				console.log(err)
				// if (err.stack && err.stack.includes('denied transaction signature')) {
				// }
				// else {
				// }
			} finally {
				this.loading = false
				this.show = false
			}
		},

		async removeAllowance () {
			// console.log('removeAllowance')
			const tokenAddress = this.input.tokenAddress
			if (!tokenAddress) {
				throw new Error ("Cannot get contract address")
			}
			const myAddress = this.myAddress
			if (!myAddress) {
				throw new Error ("Cannot get my address")
			}

			let amount = 0
			const amountBigNumber = new BigNumber(amount)
			const decimals = this.input.tokenDecimals
			const setAmount = Web3Wrapper.toBaseUnitAmount(amountBigNumber, decimals)
			this.loading = true
			try {
				const token = new ERC20TokenContract(tokenAddress, this.providerEngine);
				const gasPrice = await getCurrentGasPrice(this.network.id)
				// console.log('gasPrice: ', gasPrice)
				this.txHash = await token
					.approve(this.contractWrappers.contractAddresses.erc20Proxy, setAmount)
					.sendTransactionAsync({
						from: myAddress,
						gasPrice: gasPrice
					})
			} catch (err) {
				console.log(err)
				if (err.stack && err.stack.includes('denied transaction signature')) {
					this.loading = false
					this.show = false
					return
				}
				else {
					this.error = "Error occurred when removing allowance. Sorry!"
					return
				}
			}
			if (this.txHash) {
				await this.web3Wrapper.awaitTransactionMinedAsync(this.txHash)
				await this.loadAccount(true)
			}
			this.loading = false
			this.show = false
			this.$parent.$emit("successNotification", this.$t('compSetAllowance.successRemoveMessage', {tokenSymbol: this.input.tokenSymbol}));
		},

	},
	created () {
		// this.init()
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
